import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, Send, User, Loader2, Lock, CreditCard, 
  MessageSquare, Sparkles, CheckCircle, HelpCircle, MessageCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTION_PACKS = {
  professional: [
    { id: 'free', questions: 2, usd: 0, label: '2 Questions', description: 'Free starter pack' },
    { id: 'basic', questions: 5, usd: 10, label: '5 Questions', description: 'Deep dive into your profile' },
    { id: 'premium', questions: 10, usd: 25, label: '10 Questions', description: 'Comprehensive exploration' },
  ],
  student: [
    { id: 'free', questions: 2, usd: 0, label: '2 Questions', description: 'Free starter pack' },
    { id: 'basic', questions: 5, usd: 5, label: '5 Questions', description: 'Career clarity session' },
    { id: 'premium', questions: 10, usd: 10, label: '10 Questions', description: 'Full guidance package' },
  ],
};

const SUGGESTED_QUESTIONS = {
  professional: [
    "What are my top 3 leadership strengths?",
    "How should I approach high-pressure decisions?",
    "What type of company culture suits me best?",
    "What areas should I develop as a leader?",
    "How do I compare to successful CEOs?",
  ],
  student: [
    "What careers match my personality?",
    "What are my natural strengths?",
    "Which industries should I explore?",
    "How can I improve my weak areas?",
    "What study path suits me best?",
  ],
};

export const ReportQAPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(true);
  const [report, setReport] = useState(null);
  const [qaStats, setQaStats] = useState({ used: 0, total: 2, remaining: 2 });
  const [showPaywall, setShowPaywall] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const messagesEndRef = useRef(null);

  const isStudent = user?.user_type === 'student';
  const packs = isStudent ? QUESTION_PACKS.student : QUESTION_PACKS.professional;
  const suggestions = isStudent ? SUGGESTED_QUESTIONS.student : SUGGESTED_QUESTIONS.professional;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
      return;
    }
    if (user) {
      fetchReportAndStats();
    }
  }, [user, authLoading]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchReportAndStats = async () => {
    setLoadingReport(true);
    try {
      const reportId = searchParams.get('report_id');
      
      // Fetch report
      let reportData;
      if (reportId) {
        const reportRes = await axios.get(`${API}/report/${reportId}`);
        reportData = reportRes.data;
      } else {
        const reportsRes = await axios.get(`${API}/reports`);
        if (reportsRes.data.length > 0) {
          reportData = reportsRes.data[0];
        }
      }
      
      if (!reportData) {
        toast.error('No report found. Please complete an assessment first.');
        navigate('/instructions');
        return;
      }
      
      setReport(reportData);

      // Fetch Q&A stats
      const statsRes = await axios.get(`${API}/qa/stats?report_id=${reportData.id}`);
      setQaStats(statsRes.data);

      // Fetch chat history
      const historyRes = await axios.get(`${API}/qa/history?report_id=${reportData.id}`);
      if (historyRes.data.messages?.length > 0) {
        setMessages(historyRes.data.messages);
      } else {
        // Add welcome message
        const welcomeMsg = isStudent 
          ? `Hello ${user.full_name}! I'm here to help you understand your Career Compatibility Report. Based on our comprehensive analysis, I can answer your questions about career paths, strengths, and development areas. You have ${qaStats.remaining} free questions available.`
          : `Welcome ${user.full_name}! I'm ready to discuss your Leadership Personality Report. Our analysis provides unique leadership intelligence. You have ${qaStats.remaining} free questions available.`;
        
        setMessages([{ role: 'assistant', content: welcomeMsg }]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    // Check if user has questions remaining
    if (qaStats.remaining <= 0) {
      setShowPaywall(true);
      return;
    }

    const userMessage = { role: 'user', content: inputValue.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/qa/ask`, {
        report_id: report.id,
        question: userMessage.content,
      });

      const assistantMessage = { role: 'assistant', content: response.data.answer };
      setMessages(prev => [...prev, assistantMessage]);
      setQaStats(response.data.stats);

      // Check if questions exhausted
      if (response.data.stats.remaining <= 0) {
        setTimeout(() => setShowPaywall(true), 1000);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      toast.error(errorMsg);
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage.content);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchasePack = async (packId) => {
    if (packId === 'free') return;
    
    setProcessingPayment(true);
    try {
      const pack = packs.find(p => p.id === packId);
      const response = await axios.post(`${API}/qa/purchase`, {
        report_id: report.id,
        pack_id: packId,
        questions: pack.questions,
        amount: pack.usd,
        origin_url: window.location.origin,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (qaStats.remaining <= 0) {
      setShowPaywall(true);
      return;
    }
    setInputValue(suggestion);
  };

  if (authLoading || loadingReport) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#CC5500] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading your report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col" data-testid="report-qa-page">
      {/* Header */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/thank-you')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#CC5500]" />
              <span className="font-semibold text-white">Report Chat</span>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full border border-white/10">
              <MessageSquare className="h-4 w-4 text-[#C5A065]" />
              <span className="text-sm text-white font-medium">{qaStats.remaining}</span>
              <span className="text-xs text-gray-500">left</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Chat Container */}
      <div className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 flex flex-col">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#CC5500] to-[#C5A065] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xs">LE</span>
                </div>
              )}
              
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#CC5500] to-[#C5A065] text-white'
                    : 'bg-white/5 border border-white/10 text-gray-300'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>

              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#CC5500] to-[#C5A065] flex items-center justify-center">
                <span className="text-white font-bold text-xs">LE</span>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-[#CC5500]" />
                  <span className="text-sm text-gray-400">Analyzing your report...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions (show only if few messages) */}
        {messages.length <= 2 && qaStats.remaining > 0 && (
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Suggested questions:
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestions.slice(0, 3).map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400 hover:text-white hover:border-[#CC5500]/50 transition-all"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="relative">
          {qaStats.remaining <= 0 ? (
            <div 
              onClick={() => setShowPaywall(true)}
              className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:border-[#CC5500]/50 transition-all"
            >
              <Lock className="h-5 w-5 text-[#CC5500]" />
              <span className="text-gray-400">Purchase more questions to continue...</span>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask about your report..."
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#CC5500]"
                disabled={loading}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || loading}
                className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:opacity-90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Paywall Modal */}
      {showPaywall && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8 max-w-lg w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#CC5500]/20 to-[#C5A065]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-[#CC5500]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Unlock More Insights</h2>
              <p className="text-gray-400">
                {qaStats.used >= 2 
                  ? "You've used your free questions. Purchase a pack to continue exploring your report."
                  : "Get deeper insights into your leadership profile with additional questions."}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {packs.map((pack) => (
                <div
                  key={pack.id}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    pack.id === 'free' 
                      ? 'bg-white/5 border-white/10 opacity-50 cursor-not-allowed'
                      : 'bg-white/5 border-white/10 hover:border-[#CC5500]/50'
                  }`}
                  onClick={() => pack.id !== 'free' && handlePurchasePack(pack.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {pack.id === 'free' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <MessageSquare className="h-5 w-5 text-[#CC5500]" />
                      )}
                      <div>
                        <p className="text-white font-medium">{pack.label}</p>
                        <p className="text-xs text-gray-500">{pack.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {pack.usd === 0 ? (
                        <span className="text-green-400 font-medium">Used</span>
                      ) : (
                        <span className="text-white font-bold">${pack.usd}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowPaywall(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Maybe Later
              </Button>
              <Button
                onClick={() => handlePurchasePack('basic')}
                disabled={processingPayment}
                className="flex-1 bg-gradient-to-r from-[#CC5500] to-[#C5A065]"
              >
                {processingPayment ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Get 5 Questions
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
